import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Card from "@pathwright/ui/src/components/card/Card"
import CardHeader from "@pathwright/ui/src/components/card/CardHeader"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import Text from "@pathwright/ui/src/components/ui/Text"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import capitalize from "lodash/capitalize"
import { useEffect } from "react"
import styled from "styled-components"
import { useUserSchoolSubscriptionQuery } from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { getRelativeUrl } from "../utils/urls"
import GroupSubscriptionOrderConfirmationCard from "./GroupSubscriptionOrderConfirmationCard"
import { RenderActionButton } from "./OrderConfirmationContainer"
import OrderProcessing, { OrderProcessingLoading } from "./OrderProcessing"
import { isInFlowOrderConversion } from "./utils/in-flow-conversion"
import useOrderPollingQuery from "./useOrderPollingQuery"
import { getIntervalDisplayAdverb } from "./utils/subscription"

const NarrowCard = styled(Card)`
  & > .View {
    max-width: 500px;
  }
`

const SubscriptionConfirmationCard = ({
  card,
  children
}: {
  card: any
  children: React.ReactNode
}) => {
  const { school } = usePathwrightContext()
  const userSubscriptionQuery = useUserSchoolSubscriptionQuery({
    fetchPolicy: "network-only"
  })
  const { t } = useTranslate()
  const userSubscription = userSubscriptionQuery.data?.me?.school_subscription
  return (
    <NarrowCard card={card} navless>
      {!!userSubscription && (
        <CardHeader
          card={card}
          title={userSubscription.subscription_plan.name}
          meta={`${capitalize(
            getIntervalDisplayAdverb(
              userSubscription.subscription_plan.interval,
              t
            )
          )} subscription to ${school.name}`}
        />
      )}
      {children}
    </NarrowCard>
  )
}

export const SubscriptionConfirmation = ({
  orderId,
  renderActionButton
}: {
  orderId: number
  renderActionButton?: RenderActionButton
}) => {
  const { t } = useTranslate()
  const userSubscriptionQuery = useUserSchoolSubscriptionQuery({
    fetchPolicy: "network-only"
  })
  const userSubscription = userSubscriptionQuery.data?.me?.school_subscription

  useEffect(() => {
    if (userSubscription) {
      // force reload user subscription in subscribe store for legacy UIs
      window.App &&
        // We may already be reloading the user's subscription plan (we currently
        // do when user redeems a 100% off coupon).
        !window.App.getStore("subscribe").get("subscription_loading") &&
        window.App.getStore("subscribe").action.loadUserSubscription(
          true /* forceReload */
        )
    }
  }, [userSubscription])

  return (
    <OrderProcessing orderId={orderId}>
      {({ order }) => {
        const redirectTo = order.order_data?.redirect_to

        let primaryAction: { children: string; to: string } | React.ReactNode =
          redirectTo
            ? {
                children: t("Continue"),
                to: getRelativeUrl(redirectTo)
              }
            : {
                children: t("Get Started"),
                to: "/home/"
              }

        if (renderActionButton && isInFlowOrderConversion(order)) {
          primaryAction = renderActionButton(primaryAction)
        }

        return userSubscription ? (
          <BlankSlate
            animateIcon
            stack
            icons={null}
            icon="check"
            heading="You’re Subscribed!"
            body={
              <Text.Body style={{ textAlign: "left", maxWidth: 350 }}>
                <ul style={{ margin: "1rem 0", listStyle: "disc" }}>
                  <li>You now have 24/7 access to courses.</li>
                  <li>
                    We've sent you an email receipt with your subscription
                    information for your records.
                  </li>
                </ul>
              </Text.Body>
            }
            primaryAction={primaryAction}
          />
        ) : (
          <OrderProcessingLoading />
        )
      }}
    </OrderProcessing>
  )
}

export const SubscriptionRedeemptionConfirmationCard = ({
  card,
  orderId
}: {
  card: any
  orderId: number
}) => (
  <SubscriptionConfirmationCard card={card}>
    <SubscriptionConfirmation orderId={orderId} />
  </SubscriptionConfirmationCard>
)

export default function SubscriptionOrderConfirmationCard({
  card,
  orderId
}: {
  card: any
  orderId: number
}) {
  const orderQuery = useOrderPollingQuery(orderId)
  const order = orderQuery.data?.order

  if (orderQuery.loading && !order) {
    return (
      <NarrowCard card={card}>
        <LoadingCircle />
      </NarrowCard>
    )
  }

  if (order && order.order_type === "group_subscription") {
    return (
      <GroupSubscriptionOrderConfirmationCard card={card} orderId={orderId} />
    )
  }

  return (
    <SubscriptionConfirmationCard card={card}>
      <SubscriptionConfirmation orderId={orderId} />
    </SubscriptionConfirmationCard>
  )
}
